import { useEffect, useState, useCallback } from 'react';
import type {
  ServerAnalysisRecord,
  AnalysesStats,
  AnalysesListFilters,
  SocialPlatform,
  AnalysisActionTaken,
  EvinAnalysisResult,
} from '../../shared/types';
import { SOCIAL_PLATFORM_LABELS, SOCIAL_FORMAT_LABELS } from '../../shared/types';
import {
  listServerAnalyses,
  fetchServerAnalysesStats,
  updateServerAnalysis,
  deleteServerAnalysis,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface Props {
  onClose: () => void;
  onViewAnalysis: (result: EvinAnalysisResult, analysisType: string) => void;
}

const VERDICT_STYLES: Record<string, string> = {
  CONFORME: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200 shader:bg-emerald-500/25 shader:text-emerald-50',
  VIGILANCE: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 shader:bg-amber-500/25 shader:text-amber-50',
  RISQUE_ELEVE: 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-200 shader:bg-orange-500/25 shader:text-orange-50',
  NON_CONFORME: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200 shader:bg-red-500/25 shader:text-red-50',
};

const VERDICT_LABELS: Record<string, string> = {
  CONFORME: 'Conforme',
  VIGILANCE: 'Vigilance',
  RISQUE_ELEVE: 'Risque élevé',
  NON_CONFORME: 'Non conforme',
};

const ACTION_LABELS: Record<AnalysisActionTaken, string> = {
  pending: 'En attente',
  published: 'Publié',
  published_with_fix: 'Publié après correction',
  discarded: 'Abandonné',
};

export default function HistoriquePage({ onClose, onViewAnalysis }: Props) {
  const { features, organization } = useAuth();
  // Même règle qu'ailleurs : la feature du plan, ou un solde de crédits
  // achetés. Une découverte offerte (code promo, pool bêta) n'ouvre pas la
  // page — l'entrée du menu est déjà grisée, ceci en est le second verrou.
  const hasHistoryAccess = features.history || (organization?.creditBalance ?? 0) > 0;
  const [filters, setFilters] = useState<AnalysesListFilters>({ period: '30d' });
  const [analyses, setAnalyses] = useState<ServerAnalysisRecord[]>([]);
  const [stats, setStats] = useState<AnalysesStats | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!hasHistoryAccess) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [list, s] = await Promise.all([
        listServerAnalyses(filters),
        fetchServerAnalysesStats(filters.period ?? '30d'),
      ]);
      setAnalyses(list);
      setStats(s);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters, hasHistoryAccess]);

  useEffect(() => {
    load();
  }, [load]);

  const handleMarkAction = async (id: string, action: AnalysisActionTaken) => {
    await updateServerAnalysis(id, {
      actionTaken: action,
      publishedAt:
        action === 'published' || action === 'published_with_fix'
          ? new Date().toISOString()
          : null,
    });
    await load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette analyse de votre historique ?')) return;
    await deleteServerAnalysis(id);
    await load();
  };

  const handleUpdateTag = async (id: string, userTag: string) => {
    await updateServerAnalysis(id, { userTag: userTag || null });
    await load();
  };

  if (!hasHistoryAccess) {
    return (
      <div className="animate-fade-in max-w-2xl mx-auto text-center py-16">
        <svg className="w-10 h-10 mx-auto mb-4 text-slate-300 dark:text-slate-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h2 className="font-display text-xl text-slate-900 dark:text-white mb-2">Historique désactivé</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/80 mb-6">
          Vos analyses ne sont pas conservées avec un accès offert. Passez au plan Pro
          pour les retrouver, les annoter et les exporter.
        </p>
        <button
          onClick={onClose}
          className="font-display px-6 py-2.5 rounded-xl pastel:rounded-full text-sm text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 transition-colors"
        >
          Retour
        </button>
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-display text-2xl text-slate-900 dark:text-white flex items-center gap-2">
            📊 Historique & Conformité
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 shader:text-white/60">
            Preuve de diligence — toutes vos analyses sauvegardées
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
          aria-label="Fermer"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard
            label="Total"
            value={stats.totalCount}
            hint={stats.lastAnalysisAt ? `dernière : ${new Date(stats.lastAnalysisAt).toLocaleDateString('fr-FR')}` : ''}
          />
          <StatCard label="Score moyen" value={stats.avgScore ?? '—'} suffix="/100" />
          <StatCard
            label="Conformes"
            value={stats.conformeCount}
            className="bg-emerald-50 dark:bg-emerald-900/20 shader:bg-emerald-500/15 shader:border-emerald-400/25"
          />
          <StatCard
            label="À risque"
            value={stats.risqueEleveCount + stats.nonConformeCount}
            className="bg-red-50 dark:bg-red-900/20 shader:bg-red-500/15 shader:border-red-400/25"
          />
          <StatCard
            label="Publiés après fix"
            value={stats.fixedCount}
            className="bg-violet-50 dark:bg-violet-900/20 shader:bg-violet-500/15 shader:border-violet-400/25"
          />
        </div>
      )}

      {/* Filters */}
      <div className="mb-5 flex flex-wrap gap-2 items-center">
        <FilterPills
          label="Période"
          value={filters.period ?? 'all'}
          options={[
            { key: '7d', label: '7 jours' },
            { key: '30d', label: '30 jours' },
            { key: '90d', label: '90 jours' },
            { key: 'all', label: 'Tout' },
          ]}
          onChange={(v) => setFilters({ ...filters, period: v as AnalysesListFilters['period'] })}
        />
        <FilterPills
          label="Verdict"
          value={filters.verdict ?? ''}
          options={[
            { key: '', label: 'Tous' },
            { key: 'CONFORME', label: 'Conforme' },
            { key: 'VIGILANCE', label: 'Vigilance' },
            { key: 'RISQUE_ELEVE', label: 'Risque élevé' },
            { key: 'NON_CONFORME', label: 'Non conforme' },
          ]}
          onChange={(v) => setFilters({ ...filters, verdict: (v || undefined) as AnalysesListFilters['verdict'] })}
        />
        <FilterPills
          label="Plateforme"
          value={filters.platform ?? ''}
          options={[
            { key: '', label: 'Toutes' },
            ...(Object.entries(SOCIAL_PLATFORM_LABELS).map(([k, v]) => ({ key: k, label: v }))),
          ]}
          onChange={(v) => setFilters({ ...filters, platform: (v || undefined) as SocialPlatform | undefined })}
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-slate-500">Chargement…</div>
      ) : analyses.length === 0 ? (
        <div className="text-center py-16 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 text-slate-500">
          Aucune analyse pour ces filtres.
        </div>
      ) : (
        <ul className="space-y-2">
          {analyses.map((a) => (
            <AnalysisRow
              key={a.id}
              analysis={a}
              onView={() =>
                a.result && onViewAnalysis(a.result, a.analysisType)
              }
              onMarkAction={(action) => handleMarkAction(a.id, action)}
              onUpdateTag={(tag) => handleUpdateTag(a.id, tag)}
              onDelete={() => handleDelete(a.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────

function StatCard({
  label,
  value,
  suffix,
  hint,
  className = '',
}: {
  label: string;
  value: string | number;
  suffix?: string;
  hint?: string;
  className?: string;
}) {
  return (
    <div
      className={`p-3 rounded-xl border border-slate-200 dark:border-slate-700 shader:border-white/15 bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-xl ${className}`}
    >
      <div className="text-xs text-slate-500 dark:text-slate-400 shader:text-white">{label}</div>
      <div className="font-display text-2xl text-slate-900 dark:text-white">
        {value}
        {suffix && <span className="text-sm text-slate-400 shader:text-white/60 ml-0.5">{suffix}</span>}
      </div>
      {hint && <div className="text-[10px] text-slate-400 shader:text-white/60 mt-0.5">{hint}</div>}
    </div>
  );
}

function FilterPills<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | '';
  options: Array<{ key: T | ''; label: string }>;
  onChange: (v: T | '') => void;
}) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-xs text-slate-500 dark:text-slate-400 shader:text-white mr-1">{label} :</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
        className="text-xs rounded-lg border border-slate-300 dark:border-slate-600 shader:border-white/15 bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-xl px-2 py-1.5 text-slate-700 dark:text-slate-200 shader:text-white"
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function AnalysisRow({
  analysis,
  onView,
  onMarkAction,
  onUpdateTag,
  onDelete,
}: {
  analysis: ServerAnalysisRecord;
  onView: () => void;
  onMarkAction: (action: AnalysisActionTaken) => void;
  onUpdateTag: (tag: string) => void;
  onDelete: () => void;
}) {
  const [editingTag, setEditingTag] = useState(false);
  const [tagValue, setTagValue] = useState(analysis.userTag ?? '');

  const title = analysis.title || analysis.caption?.slice(0, 80) || '(sans titre)';
  const created = new Date(analysis.createdAt).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <li className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 shader:border-white/15 bg-white dark:bg-slate-800 shader:bg-white/10 shader:backdrop-blur-xl hover:border-violet-400 dark:hover:border-violet-500 shader:hover:border-violet-300/60 transition-colors">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {analysis.verdict && (
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-semibold ${VERDICT_STYLES[analysis.verdict] ?? ''}`}
              >
                {VERDICT_LABELS[analysis.verdict] ?? analysis.verdict}
              </span>
            )}
            {analysis.score !== null && (
              <span className="text-xs text-slate-500 dark:text-slate-400 shader:text-white/80">{analysis.score}/100</span>
            )}
            {analysis.platform && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 shader:bg-white/15 text-slate-600 dark:text-slate-300 shader:text-white">
                {SOCIAL_PLATFORM_LABELS[analysis.platform] ?? analysis.platform}
              </span>
            )}
            {analysis.socialMode && analysis.result?.socialMeta?.format && (
              <span className="text-xs text-slate-400 shader:text-white/70">
                {SOCIAL_FORMAT_LABELS[analysis.result.socialMeta.format]}
              </span>
            )}
            <span className="text-xs text-slate-400 shader:text-white/70 ml-auto">{created}</span>
          </div>
          <button
            onClick={onView}
            disabled={!analysis.result}
            className="text-left text-sm text-slate-800 dark:text-slate-200 shader:text-white font-medium hover:text-violet-600 dark:hover:text-violet-400 truncate block w-full disabled:text-slate-400 disabled:cursor-default"
          >
            {title}
          </button>
          <div className="flex items-center gap-2 mt-1.5 text-xs">
            <select
              value={analysis.actionTaken}
              onChange={(e) => onMarkAction(e.target.value as AnalysisActionTaken)}
              className="rounded border border-slate-200 dark:border-slate-600 shader:border-white/20 bg-white dark:bg-slate-900 shader:bg-white/15 px-1.5 py-0.5 text-slate-600 dark:text-slate-300 shader:text-white text-xs"
            >
              {(Object.keys(ACTION_LABELS) as AnalysisActionTaken[]).map((k) => (
                <option key={k} value={k}>
                  {ACTION_LABELS[k]}
                </option>
              ))}
            </select>
            {editingTag ? (
              <input
                value={tagValue}
                onChange={(e) => setTagValue(e.target.value)}
                onBlur={() => {
                  onUpdateTag(tagValue);
                  setEditingTag(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    onUpdateTag(tagValue);
                    setEditingTag(false);
                  }
                }}
                placeholder="Tag…"
                className="rounded border border-violet-300 shader:border-violet-300/60 bg-white dark:bg-slate-900 shader:bg-white/15 px-1.5 py-0.5 text-slate-700 dark:text-slate-200 shader:text-white text-xs w-28"
                autoFocus
              />
            ) : (
              <button
                onClick={() => setEditingTag(true)}
                className="rounded border border-slate-200 dark:border-slate-600 shader:border-white/20 px-1.5 py-0.5 text-slate-500 shader:text-white/80 hover:border-violet-400 text-xs"
              >
                {analysis.userTag ? `#${analysis.userTag}` : '+ tag'}
              </button>
            )}
          </div>
        </div>
        <button
          onClick={onDelete}
          className="text-slate-400 hover:text-red-500 transition-colors"
          aria-label="Supprimer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </button>
      </div>
    </li>
  );
}
